import {
	createContext,
	type FC,
	type PropsWithChildren,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import {
	MetaAdsAdFormat,
	MetaAdsBidStrategy,
	MetaAdsBudgetType,
	type MetaAdsCampaignDraft,
	MetaAdsCampaignObjective,
	MetaAdsConversionLocation,
	MetaAdsCTA,
	MetaAdsLocationType,
	MetaAdsOptimizationGoal,
} from "shared";
import type { StepSettings } from "../components/Stepper";

const TRAFFIC_STEPS: StepSettings[] = [
	{ title: "Campaign", icon: "campaign" },
	{ title: "Ad Set", icon: "settings" },
	{ title: "Creative", icon: "photo_library" },
	{ title: "Review", icon: "fact_check" },
];

const LEADS_INSTANT_FORM_STEPS: StepSettings[] = [
	{ title: "Campaign", icon: "campaign" },
	{ title: "Ad Set", icon: "settings" },
	{ title: "Lead Form", icon: "description" },
	{ title: "Creative", icon: "photo_library" },
	{ title: "Review", icon: "fact_check" },
];

export function getStepsForObjective(
	objective: MetaAdsCampaignObjective,
	conversionLocation: MetaAdsConversionLocation = MetaAdsConversionLocation.INSTANT_FORM,
): StepSettings[] {
	const includeLeadForm =
		objective === MetaAdsCampaignObjective.OUTCOME_LEADS &&
		conversionLocation === MetaAdsConversionLocation.INSTANT_FORM;
	const steps = includeLeadForm ? LEADS_INSTANT_FORM_STEPS : TRAFFIC_STEPS;
	return steps.map((s) => ({ ...s }));
}

function createEmptyDraft(): MetaAdsCampaignDraft {
	return {
		id: crypto.randomUUID(),
		name: "",
		objective: MetaAdsCampaignObjective.OUTCOME_TRAFFIC,
		campaignBudgetOptimization: false,
		budgetType: MetaAdsBudgetType.DAILY,
		budgetAmount: 2000,
		startDate: new Date().toISOString().split("T")[0],
		endDate: null,
		placements: "automatic",
		targetCountries: ["US"],
		ageMin: 18,
		ageMax: 65,
		genders: [0],
		optimizationGoal: MetaAdsOptimizationGoal.LINK_CLICKS,
		bidStrategy: MetaAdsBidStrategy.LOWEST_COST_WITHOUT_CAP,
		bidAmount: null,
		adFormat: MetaAdsAdFormat.SINGLE_IMAGE,
		mediaFileIds: [],
		primaryText: "",
		headline: "",
		description: "",
		displayLink: "",
		callToAction: MetaAdsCTA.LEARN_MORE,
		destinationUrl: "",
		interests: [],
		utmSource: "",
		utmMedium: "",
		utmCampaign: "",
		utmContent: "",
		conversionLocation: MetaAdsConversionLocation.INSTANT_FORM,
		pixelId: "",
		leadFormId: "",
		leadForm: null,
		leadFormMetaId: null,
		geoTargeting: {
			locationPins: [],
			locationType: MetaAdsLocationType.HOME_RECENT,
			countries: ["US"],
		},
		includedAudiences: [],
		excludedAudiences: [],
		createdAt: "",
		updatedAt: "",
	};
}

type CampaignWizardContextType = {
	draft: MetaAdsCampaignDraft;
	updateDraft: (partial: Partial<MetaAdsCampaignDraft>) => void;
	isDirty: boolean;
	currentStep: number;
	steps: StepSettings[];
	nextStep: () => void;
	prevStep: () => void;
	completeCurrentStep: () => void;
};

const CampaignWizardContext = createContext<CampaignWizardContextType | null>(
	null,
);

interface CampaignWizardProviderProps extends PropsWithChildren {
	initialOverrides?: Partial<MetaAdsCampaignDraft>;
	companyName?: string;
}

function buildDefaultCampaignName(
	companyName: string,
	objective: MetaAdsCampaignObjective,
): string {
	const objectiveLabel: Record<MetaAdsCampaignObjective, string> = {
		[MetaAdsCampaignObjective.OUTCOME_AWARENESS]: "Awareness",
		[MetaAdsCampaignObjective.OUTCOME_TRAFFIC]: "Traffic",
		[MetaAdsCampaignObjective.OUTCOME_ENGAGEMENT]: "Engagement",
		[MetaAdsCampaignObjective.OUTCOME_LEADS]: "Leads",
		[MetaAdsCampaignObjective.OUTCOME_SALES]: "Sales",
	};
	const date = new Date().toISOString().split("T")[0];
	return `${companyName} – ${objectiveLabel[objective]} – ${date}`;
}

export const CampaignWizardProvider: FC<CampaignWizardProviderProps> = ({
	children,
	initialOverrides,
	companyName = "Campaign",
}) => {
	const [isDirty, setIsDirty] = useState(false);
	const [draft, setDraft] = useState<MetaAdsCampaignDraft>(() => {
		const objective =
			initialOverrides?.objective ?? MetaAdsCampaignObjective.OUTCOME_TRAFFIC;
		return {
			...createEmptyDraft(),
			name: buildDefaultCampaignName(companyName, objective),
			...initialOverrides,
		};
	});
	const [currentStep, setCurrentStep] = useState(0);
	const [steps, setSteps] = useState<StepSettings[]>(() =>
		getStepsForObjective(
			initialOverrides?.objective ?? MetaAdsCampaignObjective.OUTCOME_TRAFFIC,
			initialOverrides?.conversionLocation ??
				MetaAdsConversionLocation.INSTANT_FORM,
		),
	);

	const updateDraft = useCallback(
		(partial: Partial<MetaAdsCampaignDraft>) => {
			setIsDirty(true);
			setDraft((prev) => {
				const next = { ...prev, ...partial };
				const objectiveChanged =
					partial.objective !== undefined &&
					partial.objective !== prev.objective;
				const conversionChanged =
					partial.conversionLocation !== undefined &&
					partial.conversionLocation !== prev.conversionLocation;

				if (objectiveChanged) {
					setCurrentStep(0);
					if (!partial.name) {
						next.name = buildDefaultCampaignName(
							companyName,
							partial.objective as MetaAdsCampaignObjective,
						);
					}
				}

				if (
					conversionChanged &&
					next.objective === MetaAdsCampaignObjective.OUTCOME_LEADS &&
					next.conversionLocation !== MetaAdsConversionLocation.INSTANT_FORM
				) {
					next.leadFormId = "";
					next.leadForm = null;
					next.leadFormMetaId = null;
				}

				if (
					objectiveChanged ||
					(conversionChanged &&
						next.objective === MetaAdsCampaignObjective.OUTCOME_LEADS)
				) {
					const newSteps = getStepsForObjective(
						next.objective,
						next.conversionLocation,
					);
					setSteps(newSteps);
					if (!objectiveChanged) {
						setCurrentStep((step) => Math.min(step, newSteps.length - 1));
					}
				}

				return next;
			});
		},
		[companyName],
	);

	const completeCurrentStep = useCallback(() => {
		setSteps((prev) =>
			prev.map((step, i) =>
				i === currentStep ? { ...step, complete: true } : step,
			),
		);
	}, [currentStep]);

	const nextStep = useCallback(() => {
		completeCurrentStep();
		setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
	}, [completeCurrentStep, steps.length]);

	const prevStep = useCallback(() => {
		setCurrentStep((prev) => Math.max(prev - 1, 0));
	}, []);

	const value = useMemo(
		() => ({
			draft,
			updateDraft,
			isDirty,
			currentStep,
			steps,
			nextStep,
			prevStep,
			completeCurrentStep,
		}),
		[
			draft,
			updateDraft,
			isDirty,
			currentStep,
			steps,
			nextStep,
			prevStep,
			completeCurrentStep,
		],
	);

	return (
		<CampaignWizardContext.Provider value={value}>
			{children}
		</CampaignWizardContext.Provider>
	);
};

export const useCampaignWizard = () => {
	const context = useContext(CampaignWizardContext);
	if (!context) {
		throw new Error(
			"useCampaignWizard must be used within a CampaignWizardProvider",
		);
	}
	return context;
};
